import { useLayoutEffect, type RefObject } from 'react';
import { hasFinePointer, isSmallScreen, prefersReducedMotion } from '@/lib/motion';

// useBentoReveal — the bento's scroll entrance (2026-09-24). The header rises
// into place, then the cards arrive in small staggered batches as they scroll
// into view: up and into full opacity.
//
// How it runs (reworked after the 2026-09-24 critiques):
//   * An IntersectionObserver notices a piece arriving and the browser's own
//     animation engine (element.animate) plays it. Opacity and transform run
//     on the compositor, so a busy main thread (the 3D hero, React, a video
//     starting) cannot stall a card half-faded. The first version tweened in
//     GSAP, whose lag smoothing slows every tween to a crawl when frames come
//     late: on a starved WebKit the cards sat blank for seconds.
//   * No ScrollTrigger positions to go stale when the hero above changes
//     height, so no refresh either. (Its page-wide refresh on body resize
//     also ran mid-scroll, which How it works found snaps the page.)
//   * Only the header text blurs, and only on desktops. The cards used to
//     rise out of a blur too, but re-rasterising a blurred card that holds a
//     playing video every frame dropped a laptop to 12-14 fps while scrolling
//     in, and left the storybook card smeared mid-viewport.
//
// Never hidden for good (round 2 critique, 2026-09-24). The observer alone
// was not enough: Chrome dropped its notification for the rap card after a
// few instant jumps (scrollbar drag, PageDown, End), and on a starved iPhone
// it sat silent for 10+ s while the peek under the hero stayed blank. So:
//   * whatever is already on screen when the bento mounts (the peek) is
//     never hidden at all;
//   * a throttled scroll/resize check, plus a slow poll, reveals any piece
//     the observer missed as soon as it overlaps the screen;
//   * a reveal that has still not finished well after it should have (its
//     animation stuck pending on a starved page) is snapped to the end.
//
// Tried and dropped (round 5, 2026-09-25): painting the waiting cards ahead
// of the scroll at an invisible 0.001 opacity, to get their raster and GPU
// shader builds out of the first scroll. Chrome does not raster content that
// far outside the screen ahead of time (even a bento shown from the start
// built as many shaders in that scroll), so it only added a raster pass.
//
// Content is visible by default. Nothing is hidden until every check has
// passed — a real browser with layout (jsdom has none), no reduced-motion
// request, the animation and observer APIs present — so tests, reduced-motion
// visitors and old browsers get the finished page. Unmount undoes everything.
//
// Markup contract, inside `root`:
//   [data-reveal-head]  header pieces, revealed together with a short stagger
//   [data-reveal]       the cards, revealed as each batch arrives
//
// whenRevealed(node) lets a card's own media wait for the card: the clips
// start playing only once their card has finished arriving (see CardClip).

const EXPO_OUT = 'cubic-bezier(0.16, 1, 0.3, 1)';
/** Touch screens start a card this far below the fold, so a quick flick
 *  never lands on a blank slot while the rise catches up. */
const AHEAD_TOUCH_PX = 80;
/** Grace after a reveal's planned end before it is snapped to its end state. */
const WATCHDOG_MS = 700;
/** The missed-notification check runs at most this often while scrolling... */
const SCROLL_CHECK_MS = 100;
/** ...and this often while nothing scrolls (a layout change above can move a
 *  card on screen without any scroll event). */
const POLL_MS = 1000;

/** Per revealed piece: settles once that piece is fully shown. */
const gates = new WeakMap<Element, Promise<void>>();

/**
 * When the card holding `node` has finished its entrance: a promise while the
 * reveal is running for it (already settled if the card never had to move),
 * or null when no reveal manages it (tests, reduced motion), so the caller
 * can simply go ahead.
 */
export function whenRevealed(node: Element): Promise<void> | null {
  const piece = node.closest('[data-reveal]');
  return (piece && gates.get(piece)) ?? null;
}

/** Document order, so a batch staggers top-to-bottom, left-to-right. */
const byPosition = (a: Element, b: Element) =>
  (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1);

export function useBentoReveal(root: RefObject<HTMLElement | null>) {
  useLayoutEffect(() => {
    const el = root.current;
    if (!el || typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    if (prefersReducedMotion()) return undefined;
    // No layout = jsdom (or a detached tree): leave everything visible.
    if (!el.getBoundingClientRect().height) return undefined;
    if (typeof el.animate !== 'function' || typeof IntersectionObserver === 'undefined') return undefined;

    const desk = hasFinePointer() && !isSmallScreen();
    const ahead = desk ? 0 : AHEAD_TOUCH_PX;
    const head = new Set(el.querySelectorAll<HTMLElement>('[data-reveal-head]'));
    const cards = Array.from(el.querySelectorAll<HTMLElement>('[data-reveal]'));
    const all = [...head, ...cards];

    const settle = new Map<HTMLElement, () => void>();
    all.forEach((n) => {
      gates.set(n, new Promise<void>((resolve) => { settle.set(n, resolve); }));
    });
    const done = (n: HTMLElement) => { settle.get(n)?.(); };

    // Content the visitor can already see never starts hidden (round 2
    // critique): on a 390x664 iPhone the chooser's heading and the top of
    // the corrido card peek under the hero, and that peek is the only cue to
    // scroll. It stays put rather than waiting on an observer that may only
    // report once the three.js chunk and its shaders have loaded.
    const vh = window.innerHeight;
    const pending = new Set<HTMLElement>();
    all.forEach((n) => {
      if (n.getBoundingClientRect().top < vh) done(n);
      else pending.add(n);
    });
    if (!pending.size) return undefined;

    const running = new Map<HTMLElement, Animation>();
    const timers = new Set<number>();
    const later = (fn: () => void, ms: number) => {
      const id = window.setTimeout(() => { timers.delete(id); fn(); }, ms);
      timers.add(id);
    };

    // Hidden until it arrives. Inline, so removing it is the whole undo.
    pending.forEach((n) => { n.style.opacity = '0'; });

    const play = (n: HTMLElement, i: number) => {
      const isHead = head.has(n);
      const duration = isHead ? 1100 : 1000;
      const delay = i * (isHead ? 80 : 90);
      const from: Keyframe = { opacity: 0, transform: `translate3d(0, ${isHead ? 22 : desk ? 56 : 40}px, 0)` };
      const to: Keyframe = { opacity: 1, transform: 'translate3d(0, 0, 0)' };
      if (isHead && desk) {
        from.filter = 'blur(8px)';
        to.filter = 'blur(0px)';
      }
      n.style.opacity = '';
      let anim: Animation;
      try {
        anim = n.animate([from, to], {
          duration,
          easing: EXPO_OUT,
          delay,
          // Holds the first frame through the stagger delay; after the end
          // nothing lingers, so the card's own styles (and the tilt) own it.
          fill: 'backwards',
        });
      } catch {
        done(n); // the browser refused the keyframes: the piece simply shows
        return;
      }
      running.set(n, anim);
      const finished = () => {
        if (running.get(n) === anim) running.delete(n);
        done(n);
      };
      anim.finished.then(finished, finished); // a cancel rejects: still shown
      // The animation can sit pending (no frame produced) on a starved page,
      // and fill: backwards keeps the piece invisible and low meanwhile.
      later(() => {
        if (running.get(n) !== anim || anim.playState === 'finished') return;
        try { anim.finish(); } catch { anim.cancel(); }
      }, delay + duration + WATCHDOG_MS);
    };

    const reveal = (batch: HTMLElement[]) => {
      batch.sort(byPosition).forEach((n, i) => {
        pending.delete(n);
        io.unobserve(n);
        play(n, i);
      });
      if (!pending.size) stopChecks();
    };

    const io = new IntersectionObserver((entries) => {
      reveal(entries
        .filter((e) => e.isIntersecting && pending.has(e.target as HTMLElement))
        .map((e) => e.target as HTMLElement));
    }, { rootMargin: `0px 0px ${ahead}px 0px` });
    pending.forEach((n) => io.observe(n));

    // The failsafe: the same test the observer makes, by hand. A piece that
    // is already above the screen (a jump scrolled past it) just shows; one
    // that overlaps the screen gets its entrance.
    const check = () => {
      if (!pending.size) return;
      const bottom = window.innerHeight + ahead;
      const arriving: HTMLElement[] = [];
      pending.forEach((n) => {
        const r = n.getBoundingClientRect();
        if (r.top >= bottom) return;
        if (r.bottom <= 0) {
          pending.delete(n);
          io.unobserve(n);
          n.style.opacity = '';
          done(n);
        } else {
          arriving.push(n);
        }
      });
      if (arriving.length) reveal(arriving);
      else if (!pending.size) stopChecks();
    };

    let lastCheck = 0;
    let trailing = 0;
    const onScroll = () => {
      const now = performance.now();
      if (now - lastCheck >= SCROLL_CHECK_MS) {
        lastCheck = now;
        check();
      }
      // One more once the scroll settles, for the frame the throttle skipped.
      window.clearTimeout(trailing);
      trailing = window.setTimeout(() => { lastCheck = performance.now(); check(); }, SCROLL_CHECK_MS + 40);
    };
    const poll = window.setInterval(check, POLL_MS);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    // Keyboard focus never lands on an invisible card (round 3 critique,
    // 2026-09-25): tabbing into a card that is still hidden, or whose
    // entrance sits pending on a busy page, left its focus ring inside a
    // transparent card for ~2 s. Focus shows the card at once, finished.
    const onFocusIn = (e: FocusEvent) => {
      const piece = (e.target as Element | null)?.closest?.('[data-reveal], [data-reveal-head]') as HTMLElement | null;
      if (!piece || !el.contains(piece)) return;
      if (pending.has(piece)) {
        pending.delete(piece);
        io.unobserve(piece);
        piece.style.opacity = '';
        done(piece);
        if (!pending.size) stopChecks();
      }
      const anim = running.get(piece);
      if (anim) {
        try { anim.finish(); } catch { anim.cancel(); }
      }
    };
    el.addEventListener('focusin', onFocusIn);

    let checking = true;
    function stopChecks() {
      if (!checking) return;
      checking = false;
      io.disconnect();
      window.clearInterval(poll);
      window.clearTimeout(trailing);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    }

    return () => {
      stopChecks();
      el.removeEventListener('focusin', onFocusIn);
      timers.forEach((id) => window.clearTimeout(id));
      running.forEach((a) => a.cancel());
      all.forEach((n) => {
        n.style.opacity = '';
        done(n);
        gates.delete(n);
      });
    };
  }, [root]);
}
